import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen } from 'lucide-react';

const SplashScreen = ({ finishLoading }) => {
  useEffect(() => {
    const t = setTimeout(finishLoading, 3200);
    return () => clearTimeout(t);
  }, [finishLoading]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#07090f' }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.04 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* ── Animated background blobs ── */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 700, height: 700,
          background: 'radial-gradient(circle, rgba(45,136,255,0.08) 0%, transparent 65%)',
        }}
        animate={{ scale: [1, 1.15, 1], x: [-60, 60, -60], y: [-40, 40, -40] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(30,80,200,0.06) 0%, transparent 65%)',
        }}
        animate={{ scale: [1.1, 1, 1.1], x: [60, -60, 60], y: [40, -40, 40] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ── Subtle grid texture ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* ── Thin top accent line ── */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(45,136,255,0.7), transparent)' }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* ── Center content ── */}
      <div className="flex flex-col items-center relative z-10">

        {/* ── Icon cluster ── */}
        <div className="relative mb-10">
          {/* Outer glow ring */}
          <motion.div
            className="absolute rounded-full pointer-events-none"
            style={{
              inset: -32,
              background: 'radial-gradient(circle, rgba(45,136,255,0.18) 0%, transparent 70%)',
              filter: 'blur(16px)',
            }}
            animate={{ opacity: [0.5, 1, 0.5], scale: [0.95, 1.05, 0.95] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Pulsing border rings */}
          <motion.div
            className="absolute rounded-2xl border border-blue-500/15"
            style={{ inset: -20 }}
            animate={{ opacity: [0.3, 0.8, 0.3], scale: [1, 1.07, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute rounded-2xl border border-blue-400/20"
            style={{ inset: -10 }}
            animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.04, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
          />

          {/* Main icon box */}
          <motion.div
            className="relative w-24 h-24 rounded-2xl flex flex-col items-center justify-center gap-0.5"
            style={{
              background: 'linear-gradient(145deg, rgba(10,30,80,0.9) 0%, rgba(5,15,40,0.95) 100%)',
              border: '1px solid rgba(45,136,255,0.45)',
              boxShadow: '0 0 60px rgba(45,136,255,0.22), 0 0 120px rgba(45,136,255,0.10), inset 0 0 40px rgba(45,136,255,0.08)',
            }}
            initial={{ scale: 0, rotate: -90, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22, delay: 0.15 }}
          >
            {/* Sparkle dot */}
            <motion.div
              className="absolute top-2 right-2.5 w-1.5 h-1.5 rounded-full bg-white"
              style={{ boxShadow: '0 0 6px 3px rgba(180,220,255,0.9)' }}
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
            />

            <GraduationCap
              size={28}
              className="text-blue-400"
              style={{ filter: 'drop-shadow(0 0 8px rgba(45,136,255,0.9))' }}
            />
            <BookOpen
              size={22}
              className="text-blue-300/80"
              style={{ filter: 'drop-shadow(0 0 6px rgba(45,136,255,0.7))' }}
            />
          </motion.div>
        </div>

        {/* ── Brand name ── */}
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1
            className="text-5xl md:text-6xl font-extrabold tracking-tight leading-none select-none"
            style={{ letterSpacing: '-0.02em' }}
          >
            {/* SMART — silver metallic */}
            <span
              style={{
                background: 'linear-gradient(135deg, #b0bec5 0%, #ffffff 40%, #90a4ae 70%, #cfd8dc 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              SMART{' '}
            </span>
            {/* EDU — blue */}
            <span
              style={{
                background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 50%, #1e88e5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: 'none',
                filter: 'drop-shadow(0 0 18px rgba(45,136,255,0.6))',
              }}
            >
              EDU{' '}
            </span>
            {/* NEXT — silver metallic */}
            <span
              style={{
                background: 'linear-gradient(135deg, #b0bec5 0%, #ffffff 40%, #90a4ae 70%, #cfd8dc 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              NEXT
            </span>
          </h1>
        </motion.div>

        {/* ── Tagline ── */}
        <motion.div
          className="mt-3 flex items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.8 }}
        >
          <div className="h-px w-8" style={{ background: 'linear-gradient(90deg, transparent, rgba(45,136,255,0.6))' }} />
          <p className="text-[10px] uppercase tracking-[0.4em] font-semibold text-gray-500 select-none">
            Learn &bull; Grow &bull; Succeed
          </p>
          <div className="h-px w-8" style={{ background: 'linear-gradient(90deg, rgba(45,136,255,0.6), transparent)' }} />
        </motion.div>

        {/* ── Progress bar ── */}
        <motion.div
          className="mt-14 relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="w-52 h-[2px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <motion.div
              className="h-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, rgba(45,136,255,0.5) 0%, #2d88ff 50%, rgba(100,160,255,0.9) 100%)',
                boxShadow: '0 0 12px rgba(45,136,255,0.8)',
              }}
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ delay: 1.25, duration: 1.7, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <motion.div
            className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-400"
            style={{ boxShadow: '0 0 8px 3px rgba(45,136,255,0.8)' }}
            initial={{ left: '0%' }}
            animate={{ left: '100%' }}
            transition={{ delay: 1.25, duration: 1.7, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SplashScreen;
